import { DROPIT_STATUS_META } from "./orderDetailsHelpers";

// Shadowfax's own raw status_id → friendly label. Label-only copy of
// ShadowfaxShipmentCard.jsx's STATUS_STYLES (kept separate rather than
// imported from a component file — this is a fixed 3rd-party vocabulary,
// same category as DROPIT_STATUS_META, not seller-configurable data, so a
// small duplication here is the same tradeoff already accepted there).
const SHADOWFAX_STATUS_LABELS = {
  new: "Booked",
  assigned_for_seller_pickup: "Pickup Scheduled",
  ofp: "Pickup Scheduled",
  picked: "Picked Up",
  assigned_for_delivery: "Assigned for Delivery",
  ofd: "Out for Delivery",
  delivered: "Delivered",
  cancelled_by_seller: "Cancelled",
  cancelled_by_customer: "Cancelled",
  rts: "Returning to You",
  rts_d: "Returned to You",
  lost: "Lost in Transit",
};

// Shiprocket's own raw status → friendly label. Label-only copy of
// exportOrderPdf.js's SHIPMENT_STATUS_LABELS, same reasoning as above.
const SHIPROCKET_STATUS_LABELS = {
  CREATED: "Created",
  AWB_ASSIGNED: "AWB Assigned",
  PICKUP_GENERATED: "Pickup Scheduled",
  MANIFEST_GENERATED: "Manifest Generated",
  IN_TRANSIT: "In Transit",
  OUT_FOR_DELIVERY: "Out for Delivery",
  DELIVERED: "Delivered",
  CANCELLED: "Cancelled",
  RTO_INITIATED: "RTO Initiated",
  RTO_DELIVERED: "RTO Delivered",
  LOST: "Lost",
};

// "assigned_for_seller_pickup" -> "Assigned For Seller Pickup" — last-resort
// fallback for any raw value not in the maps above (a new status Shadowfax/
// Shiprocket adds that this app hasn't been updated for yet), so the badge
// still shows something readable instead of a raw enum string.
function prettify(raw) {
  return String(raw || "")
    .split(/[_\s]+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ""))
    .join(" ") || null;
}

const PROVIDER_LABEL_MAPS = {
  Dropit: (raw) => DROPIT_STATUS_META[raw]?.label,
  Shadowfax: (raw) => SHADOWFAX_STATUS_LABELS[raw],
  Shiprocket: (raw) => SHIPROCKET_STATUS_LABELS[raw],
};

/**
 * Resolves the public tracking page's `providerStatus` (see
 * DropitShipmentService.getPublicTrackingData) into "Rider Status: <label>" —
 * null when there's no provider status to show at all (a manually-selected
 * carrier with no live integration, e.g. Delhivery/FedEx/DHL, never has one),
 * so the badge section can be left out entirely rather than rendered empty.
 *
 * Deliberately always "Rider Status", never the provider's own name (was
 * "Dropit Status"/"Shadowfax Status"/"Shiprocket Status" before) — the
 * customer doesn't need to know which courier is being used, and a generic
 * label means this badge never needs to change again if a new provider is
 * added later.
 */
export function resolveProviderStatusText(providerStatus) {
  if (!providerStatus?.provider || !providerStatus?.rawStatus) return null;
  const resolver = PROVIDER_LABEL_MAPS[providerStatus.provider];
  const label = resolver?.(providerStatus.rawStatus) || prettify(providerStatus.rawStatus);
  if (!label) return null;
  return `Rider Status: ${label}`;
}
